import { useState } from 'react';
import CountdownTimer from './CountdownTimer';
import { useCart } from '../../../contexts/CartContext';

function applyDiscount(price: string): string {
  const num = parseFloat(price.replace(/[$,]/g, ''));
  const discounted = Math.floor(num * 0.85);
  return '$' + discounted.toLocaleString();
}

export default function MembershipTiers() {
  const [timerExpired, setTimerExpired] = useState(false);
  const { addToCart } = useCart();

  const tiers = [
    {
      id: 'vitamin-basic',
      name: 'Basic Plan',
      ivCount: 'Your choice of 1 IV Treatment',
      price: '$299',
      period: 'Monthly',
      features: [
        '3 Month Minimum',
        'Hydration',
        'Hangover Relief + B vitamins',
        'Food Poisoning',
        'Headache + Migraine Relief',
        'Immunity',
        'Myer\'s Cocktail',
        'Beauty',
        '15% off additional services',
      ],
      cta: 'Add to Cart',
      isPremium: false,
    },
    {
      id: 'vitamin-restore',
      name: 'Restore Plan',
      ivCount: 'Your choice of 2 IV Treatments',
      price: '$549',
      period: 'Monthly',
      features: [
        '3 Month Minimum',
        'Hydration',
        'Hangover Relief + B vitamins',
        'Food Poisoning',
        'Headache + Migraine Relief',
        'Immunity',
        'Myer\'s Cocktail',
        'Beauty',
        '15% off additional services',
      ],
      cta: 'Add to Cart',
      isPremium: false,
    },
    {
      id: 'vitamin-executive',
      name: 'Executive Plan',
      ivCount: 'Your choice of 4 IV Treatments',
      price: '$1,000',
      period: 'Monthly',
      features: [
        '3 Month Minimum',
        'Hydration',
        'Hangover Relief + B vitamins',
        'Food Poisoning',
        'Headache + Migraine Relief',
        'Immunity',
        'Myer\'s Cocktail',
        'Beauty',
        '15% off additional services',
      ],
      cta: 'Add to Cart',
      isPremium: true,
    },
    {
      id: 'vitamin-elite',
      name: 'Elite Plan',
      ivCount: 'Your choice of 8 IV Treatments',
      price: '$1,750',
      period: 'Monthly',
      features: [
        '3 Month Minimum',
        'Hydration',
        'Hangover Relief + B vitamins',
        'Food Poisoning',
        'Headache + Migraine Relief',
        'Immunity',
        'Myer\'s Cocktail',
        'Beauty',
        '15% off additional services',
      ],
      cta: 'Add to Cart',
      isPremium: false,
    },
  ];

  const handleAddToCart = (tier: typeof tiers[0]) => {
    addToCart({
      id: tier.id,
      name: tier.name,
      type: 'vitamin',
      ivCount: tier.ivCount,
      price: tier.price,
      period: tier.period,
      features: tier.features,
      discountedPrice: !timerExpired ? applyDiscount(tier.price) : undefined,
    });
  };

  return (
    <section className="py-24 px-6 lg:px-12 bg-sand">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-light text-charcoal mb-6 tracking-wide">
            Vitamin IV Memberships
          </h2>
          <p className="text-greige text-lg font-light leading-relaxed max-w-2xl mx-auto">
            Maintaining good health should always remain a top priority. IV Hydration Club&apos;s membership plans offer perks and privileges to doing just that.
          </p>
        </div>

        {/* Timer banner ABOVE the grid */}
        <div className="mb-10 flex flex-col items-center gap-3">
          <div className="flex items-center gap-2 bg-gold/10 border border-gold/30 rounded-full px-6 py-3">
            <i className="ri-time-line text-gold text-lg w-5 h-5 flex items-center justify-center"></i>
            <p className="text-charcoal text-sm font-light tracking-wide">
              If you book within 10 minutes, get <strong className="font-semibold text-gold">15% off</strong>
            </p>
          </div>
          <CountdownTimer onExpire={() => setTimerExpired(true)} compact />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {tiers.map((tier, index) => (
            <div
              key={index}
              className={`relative overflow-hidden rounded-2xl p-8 transition-all duration-400 hover:shadow-2xl hover:-translate-y-2 ${
                tier.isPremium
                  ? 'bg-charcoal text-white border-2 border-gold'
                  : 'bg-bone text-charcoal'
              }`}
            >
              {/* Members Only ribbon */}
              <div className="absolute top-4 right-[-28px] rotate-45 z-10">
                <div className={`px-8 py-1 text-[9px] font-light tracking-[0.2em] uppercase whitespace-nowrap ${
                  tier.isPremium
                    ? 'bg-gold text-white'
                    : 'bg-charcoal/80 text-white'
                }`}>
                  Members Only
                </div>
              </div>

              {tier.isPremium && (
                <div className="text-center mb-4">
                  <span className="px-4 py-1 bg-gold text-white text-xs font-light tracking-widest rounded-full whitespace-nowrap">
                    MOST POPULAR
                  </span>
                </div>
              )}

              <h3 className="text-2xl font-light tracking-wide mb-2 text-center">
                {tier.name}
              </h3>

              <p className={`text-center text-sm font-light mb-4 ${tier.isPremium ? 'text-white/70' : 'text-greige'}`}>
                {tier.ivCount}
              </p>

              <div className="text-center mb-6">
                {!timerExpired ? (
                  <>
                    <div className="flex items-center justify-center gap-2 mb-1">
                      <span className={`text-lg font-light line-through opacity-50 ${tier.isPremium ? 'text-white' : 'text-greige'}`}>
                        {tier.price}
                      </span>
                      <span className="text-xs bg-gold/20 text-gold border border-gold/40 rounded-full px-2 py-0.5 font-light tracking-wide whitespace-nowrap">
                        15% OFF
                      </span>
                    </div>
                    <span className={`text-4xl font-light ${tier.isPremium ? 'text-gold' : 'text-charcoal'}`}>
                      {applyDiscount(tier.price)}
                    </span>
                    <span className={`text-sm font-light ml-2 ${tier.isPremium ? 'text-white/70' : 'text-greige'}`}>
                      {tier.period}
                    </span>
                  </>
                ) : (
                  <>
                    <span className={`text-4xl font-light ${tier.isPremium ? 'text-gold' : 'text-charcoal'}`}>
                      {tier.price}
                    </span>
                    <span className={`text-sm font-light ml-2 ${tier.isPremium ? 'text-white/70' : 'text-greige'}`}>
                      {tier.period}
                    </span>
                  </>
                )}
              </div>

              <ul className="space-y-3 mb-8">
                {tier.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start">
                    <i className={`ri-check-line text-lg mr-2 flex-shrink-0 ${tier.isPremium ? 'text-gold' : 'text-gold'}`}></i>
                    <span className={`text-sm font-light leading-relaxed ${tier.isPremium ? 'text-white/90' : 'text-greige'}`}>
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleAddToCart(tier)}
                className={`block w-full py-3 text-center text-sm font-light tracking-wide rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  tier.isPremium
                    ? 'bg-gold text-white hover:bg-gold/90'
                    : 'bg-charcoal text-white hover:bg-charcoal/90'
                }`}
              >
                {tier.cta}
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}